import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import { toast } from "react-toastify";

const AllPurchaseOrders = () => {
    const [loading, setLoading] = useState(false);

    const [rows, setRows] = useState([]);

    const [products, setProducts] = useState([]);

    const [filters, setFilters] = useState({
        start_date: "",
        end_date: "",
        product_id: "",
    });

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    /* =========================================================
       GET PRODUCTS
    ========================================================= */

    useEffect(() => {
        getProducts();
    }, []);

    const getProducts = async () => {
        const token = getAuthToken();

        if (!token) {
            return;
        }

        try {
            const response = await axios.get(
                `${config.API}/products`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data?.success) {
                setProducts(response.data.data || []);
            }
        } catch (error) {
            console.error(
                "Products Error:",
                error
            );
        }
    };

    /* =========================================================
       GET PURCHASE ORDERS
    ========================================================= */

    const getPurchaseOrders = async (page = 1) => {
        const token = getAuthToken();

        if (!token) {
            toast.error(
                "Authentication token not found."
            );
            return;
        }

        try {
            setLoading(true);

            const payload = {
                start_date:
                    filters.start_date || null,

                end_date:
                    filters.end_date || null,

                product_id:
                    filters.product_id || null,

                page: Number(page),

                per_page: 10,
            };

            console.log(
                "Purchase Orders Request:",
                payload
            );

            const response = await axios.post(
                `${config.API}/senmanager/allpurchaseorders`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log(
                "Purchase Orders Response:",
                response.data
            );

            if (response.data?.success) {

                const data =
                    response.data?.data || [];

                setRows(
                    Array.isArray(data)
                        ? data
                        : []
                );

                /*
                 * Supports Laravel pagination response:
                 *
                 * pagination: {
                 *   current_page,
                 *   last_page,
                 *   per_page,
                 *   total
                 * }
                 */

                if (response.data?.pagination) {

                    setPagination({
                        current_page:
                            Number(
                                response.data.pagination
                                    .current_page
                            ) || 1,

                        last_page:
                            Number(
                                response.data.pagination
                                    .last_page
                            ) || 1,

                        per_page:
                            Number(
                                response.data.pagination
                                    .per_page
                            ) || 20,

                        total:
                            Number(
                                response.data.pagination
                                    .total
                            ) || 0,
                    });

                } else if (
                    response.data?.meta
                ) {

                    setPagination({
                        current_page:
                            Number(
                                response.data.meta
                                    .current_page
                            ) || 1,

                        last_page:
                            Number(
                                response.data.meta
                                    .last_page
                            ) || 1,

                        per_page:
                            Number(
                                response.data.meta
                                    .per_page
                            ) || 20,

                        total:
                            Number(
                                response.data.meta
                                    .total
                            ) || 0,
                    });
                }

            } else {

                setRows([]);

                toast.warning(
                    response.data?.message ||
                    "No purchase orders found."
                );
            }

        } catch (error) {

            console.error(
                "Purchase Orders Error:",
                error
            );

            setRows([]);

            toast.error(
                error.response?.data?.message ||
                "Unable to load purchase orders."
            );

        } finally {
            setLoading(false);
        }
    };

    /* =========================================================
       SEARCH
    ========================================================= */

    const handleSearch = () => {
        getPurchaseOrders(1);
    };

    /* =========================================================
       CLEAR FILTERS
    ========================================================= */

    const clearFilters = () => {

        setFilters({
            start_date: "",
            end_date: "",
            product_id: "",
        });

        setRows([]);

        setPagination({
            current_page: 1,
            last_page: 1,
            per_page: 20,
            total: 0,
        });
    };

    /* =========================================================
       FORMAT DATE
    ========================================================= */

    const formatDate = (date) => {

        if (!date) {
            return "-";
        }

        const parsedDate =
            new Date(date);

        if (
            isNaN(
                parsedDate.getTime()
            )
        ) {
            return date;
        }

        return parsedDate.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }
        );
    };

    /* =========================================================
       PAGINATION
    ========================================================= */

    const renderPagination = () => {

        if (
            pagination.last_page <= 1
        ) {
            return null;
        }

        const currentPage =
            pagination.current_page;

        const lastPage =
            pagination.last_page;

        const pages = [];

        let startPage =
            Math.max(
                1,
                currentPage - 2
            );

        let endPage =
            Math.min(
                lastPage,
                currentPage + 2
            );

        if (currentPage <= 2) {
            endPage =
                Math.min(
                    lastPage,
                    5
                );
        }

        if (
            currentPage >=
            lastPage - 1
        ) {
            startPage =
                Math.max(
                    1,
                    lastPage - 4
                );
        }

        for (
            let i = startPage;
            i <= endPage;
            i++
        ) {
            pages.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">

                {/* RECORD INFO */}

                <div className="text-muted small">

                    Showing{" "}

                    {pagination.total > 0
                        ? (
                            (
                                currentPage -
                                1
                            ) *
                                pagination.per_page +
                            1
                        )
                        : 0}

                    {" "}to{" "}

                    {Math.min(
                        currentPage *
                            pagination.per_page,
                        pagination.total
                    )}

                    {" "}of{" "}

                    {pagination.total}

                    {" "}records

                </div>


                {/* PAGINATION */}

                <nav>

                    <ul className="pagination pagination-sm mb-0">

                        {/* PREVIOUS */}

                        <li
                            className={`page-item ${
                                currentPage === 1
                                    ? "disabled"
                                    : ""
                            }`}
                        >

                            <button
                                type="button"
                                className="page-link"
                                onClick={() =>
                                    getPurchaseOrders(
                                        currentPage -
                                            1
                                    )
                                }
                                disabled={
                                    currentPage ===
                                    1
                                }
                            >
                                Previous
                            </button>

                        </li>


                        {/* FIRST PAGE */}

                        {startPage > 1 && (
                            <>
                                <li className="page-item">

                                    <button
                                        type="button"
                                        className="page-link"
                                        onClick={() =>
                                            getPurchaseOrders(
                                                1
                                            )
                                        }
                                    >
                                        1
                                    </button>

                                </li>

                                {startPage > 2 && (
                                    <li className="page-item disabled">
                                        <span className="page-link">
                                            ...
                                        </span>
                                    </li>
                                )}
                            </>
                        )}


                        {/* PAGE NUMBERS */}

                        {pages.map((page) => (

                            <li
                                key={page}
                                className={`page-item ${
                                    page ===
                                    currentPage
                                        ? "active"
                                        : ""
                                }`}
                            >

                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() =>
                                        getPurchaseOrders(
                                            page
                                        )
                                    }
                                >
                                    {page}
                                </button>

                            </li>

                        ))}


                        {/* LAST PAGE */}

                        {endPage <
                            lastPage && (
                            <>
                                {endPage <
                                    lastPage -
                                        1 && (
                                    <li className="page-item disabled">
                                        <span className="page-link">
                                            ...
                                        </span>
                                    </li>
                                )}

                                <li className="page-item">

                                    <button
                                        type="button"
                                        className="page-link"
                                        onClick={() =>
                                            getPurchaseOrders(
                                                lastPage
                                            )
                                        }
                                    >
                                        {lastPage}
                                    </button>

                                </li>
                            </>
                        )}


                        {/* NEXT */}

                        <li
                            className={`page-item ${
                                currentPage ===
                                lastPage
                                    ? "disabled"
                                    : ""
                            }`}
                        >

                            <button
                                type="button"
                                className="page-link"
                                onClick={() =>
                                    getPurchaseOrders(
                                        currentPage +
                                            1
                                    )
                                }
                                disabled={
                                    currentPage ===
                                    lastPage
                                }
                            >
                                Next
                            </button>

                        </li>

                    </ul>

                </nav>

            </div>
        );
    };

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <div className="container-fluid mt-4">

            {/* PAGE HEADER */}

            <div className="mb-4">

                <h4 className="fw-semibold mb-1">
                    All Purchase Orders
                </h4>

                <p className="text-muted mb-0">
                    View and filter all purchase orders.
                </p>

            </div>


            {/* FILTER CARD */}

            <div className="card border-0 shadow-sm mb-4">

                <div className="card-body p-4">

                    <div className="d-flex justify-content-between align-items-center mb-4">

                        <div>

                            <h6 className="fw-semibold mb-1">
                                Purchase Order Filters
                            </h6>

                            <small className="text-muted">
                                Filter purchase orders by date and product.
                            </small>

                        </div>

                        <button
                            type="button"
                            className="btn btn-light btn-sm border"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>

                    </div>


                    <div className="row g-3 align-items-end">

                        {/* START DATE */}

                        <div className="col-12 col-md-3">

                            <label className="form-label fw-semibold">
                                Start Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={
                                    filters.start_date
                                }
                                onChange={(e) =>
                                    setFilters(
                                        (prev) => ({
                                            ...prev,
                                            start_date:
                                                e
                                                    .target
                                                    .value,
                                        })
                                    )
                                }
                            />

                        </div>


                        {/* END DATE */}

                        <div className="col-12 col-md-3">

                            <label className="form-label fw-semibold">
                                End Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={
                                    filters.end_date
                                }
                                onChange={(e) =>
                                    setFilters(
                                        (prev) => ({
                                            ...prev,
                                            end_date:
                                                e
                                                    .target
                                                    .value,
                                        })
                                    )
                                }
                            />

                        </div>


                        {/* PRODUCT */}

                        <div className="col-12 col-md-3">

                            <label className="form-label fw-semibold">
                                Product
                            </label>

                            <select
                                className="form-select"
                                value={
                                    filters.product_id
                                }
                                onChange={(e) =>
                                    setFilters(
                                        (prev) => ({
                                            ...prev,
                                            product_id:
                                                e
                                                    .target
                                                    .value,
                                        })
                                    )
                                }
                            >

                                <option value="">
                                    All Products
                                </option>

                                {products.map(
                                    (item) => (
                                        <option
                                            key={
                                                item.id
                                            }
                                            value={
                                                item.id
                                            }
                                        >
                                            {item.name ||
                                                item.product_name}
                                        </option>
                                    )
                                )}

                            </select>

                        </div>


                        {/* SEARCH */}

                        <div className="col-12 col-md-3">

                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={
                                    handleSearch
                                }
                                disabled={loading}
                            >

                                {loading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />

                                        Loading...
                                    </>
                                ) : (
                                    "Search"
                                )}

                            </button>

                        </div>

                    </div>

                </div>

            </div>


            {/* TABLE */}

            <div className="card border-0 shadow-sm">

                <div className="card-header bg-white border-0 p-4">

                    <div className="d-flex justify-content-between align-items-center">

                        <div>

                            <h5 className="fw-semibold mb-1">
                                Purchase Orders
                            </h5>

                            <small className="text-muted">

                                {filters.start_date &&
                                filters.end_date
                                    ? `${filters.start_date} → ${filters.end_date}`
                                    : "All purchase orders"}

                            </small>

                        </div>


                        <span className="badge bg-light text-dark border">

                            {pagination.total} Records

                        </span>

                    </div>

                </div>


                <div className="card-body p-0">

                    {loading ? (

                        <div className="text-center py-5">

                            <div
                                className="spinner-border text-primary"
                                role="status"
                            />

                            <div className="text-muted mt-3">
                                Loading purchase orders...
                            </div>

                        </div>

                    ) : (

                        <div className="table-responsive">

                            <table className="table table-hover align-middle mb-0">

                                <thead className="table-light">

                                    <tr>

                                        <th
                                            className="px-4"
                                            style={{
                                                width: "70px",
                                            }}
                                        >
                                            #
                                        </th>

                                        <th>
                                            PO NUMBER
                                        </th>

                                        <th>
                                            PRODUCT
                                        </th>

                                        <th>
                                            CONSIGNEE
                                        </th>

                                        <th>
                                            DATE
                                        </th>

                                        <th className="text-end">
                                            QUANTITY
                                        </th>

                                        <th>
                                            STATUS
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {rows.length > 0 ? (

                                        rows.map(
                                            (
                                                item,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        item.id ??
                                                        index
                                                    }
                                                >

                                                    <td className="px-4 text-muted">

                                                        {(
                                                            pagination.current_page -
                                                                1
                                                        ) *
                                                            pagination.per_page +
                                                            index +
                                                            1}

                                                    </td>


                                                    <td>

                                                        <span className="fw-semibold">

                                                            {
                                                                item.po_number ??
                                                                item.purchase_order_number ??
                                                                item.order_number ??
                                                                "-"
                                                            }

                                                        </span>

                                                    </td>


                                                    <td>

                                                        {
                                                            item.product_name ??
                                                            item.product ??
                                                            "-"
                                                        }

                                                    </td>


                                                    <td>

                                                        {
                                                            item.consignee ??
                                                            "-"
                                                        }

                                                    </td>


                                                    <td>

                                                        {formatDate(
                                                            item.created_at ??
                                                                item.date ??
                                                                item.order_date
                                                        )}

                                                    </td>


                                                    <td className="text-end fw-semibold">

                                                        {Number(
                                                            item.quantity ??
                                                                item.total_quantity ??
                                                                0
                                                        ).toLocaleString()}

                                                    </td>


                                                    <td>

                                                        {item.delivery_status ? (
                                                            <span className="badge bg-success-subtle text-success">
                                                                {
                                                                    item.delivery_status
                                                                }
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )}

                                                    </td>

                                                </tr>

                                            )
                                        )

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan="7"
                                                className="text-center text-muted py-5"
                                            >

                                                No purchase orders found.

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </div>


                {/* PAGINATION */}

                {!loading &&
                    rows.length > 0 &&
                    renderPagination()}

            </div>

        </div>
    );
};

export default AllPurchaseOrders;